import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  List,
  Badge,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  Tabs,
  Tab,
  Divider,
  ListItem,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CircularProgress from "@mui/material/CircularProgress";
import api from "../config/axiosConfig";
import NotificationListItem from "./Common/NotificationListItem";

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: ALL, 1: UNSEEN
  const [unreadCount, setUnreadCount] = useState(0);

  // Unified, keyed state per tab to reduce duplication
  const [lists, setLists] = useState({ all: [], unseen: [] });
  const [pages, setPages] = useState({ all: 1, unseen: 1 });
  const [loading, setLoading] = useState({ all: false, unseen: false });
  const [loadingMore, setLoadingMore] = useState({ all: false, unseen: false });
  const [hasMore, setHasMore] = useState({ all: false, unseen: false });

  const pageSize = 10; // as per requirement

  const mapApiItemToUi = useCallback((item) => item || {}, []);

  // Refs to avoid stale closures in interval
  const unreadCountRef = useRef(unreadCount);
  const activeTabRef = useRef(activeTab);
  const fetchListRef = useRef(null);
  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Helper to reset list-related state to prevent duplicates before refetch
  const resetNotificationState = useCallback(() => {
    setLists({ all: [], unseen: [] });
    setPages({ all: 1, unseen: 1 });
    setHasMore({ all: false, unseen: false });
    setLoading({ all: false, unseen: false });
    setLoadingMore({ all: false, unseen: false });
  }, []);

  // Generic list fetcher for both tabs to avoid duplicate logic
  const fetchList = useCallback(
    async (key = "all", targetPage = 1, isLoadMore = false) => {
      // Avoid duplicate fetch for the same key/state
      if (isLoadMore ? loadingMore[key] : loading[key]) return;
      try {
        if (isLoadMore) {
          setLoadingMore((s) => ({ ...s, [key]: true }));
        } else {
          setLoading((s) => ({ ...s, [key]: true }));
        }
        const params = {
          page: targetPage,
          limit: pageSize,
        };
        const endpoint =
          key === "all" ? "/api/notifications" : "/api/notifications/unread";
        const { data } = await api.get(endpoint, { params });
        const list = data.data;
        const normalized = list.map(mapApiItemToUi);

        setLists((prev) => ({
          ...prev,
          [key]: targetPage === 1 ? normalized : [...prev[key], ...normalized],
        }));

        const total = data?.total;
        const currentPage = data?.page ?? targetPage;
        const limit = data?.limit ?? pageSize;
        const unreadTotal = data?.unread_total;
        const hasNextMeta = data?.has_next_page ?? data?.hasNextPage;

        setHasMore((prev) => ({
          ...prev,
          [key]:
            typeof hasNextMeta === "boolean"
              ? hasNextMeta
              : typeof total === "number"
              ? (currentPage - 1) * limit + normalized.length < total
              : normalized.length === limit,
        }));

        setPages((prev) => ({ ...prev, [key]: targetPage }));

        if (typeof unreadTotal === "number") {
          setUnreadCount(unreadTotal);
        }
      } catch (err) {
        console.error(`Failed to fetch ${key} notifications:`, err);
      } finally {
        if (isLoadMore) {
          setLoadingMore((s) => ({ ...s, [key]: false }));
        } else {
          setLoading((s) => ({ ...s, [key]: false }));
        }
      }
    },
    [loading, loadingMore, mapApiItemToUi]
  );

  // Keep a stable reference to fetchList so effects don't depend on it
  useEffect(() => {
    fetchListRef.current = fetchList;
  }, [fetchList]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/api/notifications/unread/count");
      const count =
        typeof data?.unreadCount === "number" ? data.unreadCount : 0;
      setUnreadCount(count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  // Initial load effect (run once)
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([
          fetchUnreadCount(),
          fetchListRef.current
            ? fetchListRef.current("all", 1, false)
            : Promise.resolve(),
        ]);
      } catch (e) {
        // noop, individual functions already log
      }
    })();
  }, [fetchUnreadCount]);

  // Polling effect (run once, interval every 60s)
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const { data } = await api.get("/api/notifications/unread/count");
        const newCount =
          typeof data?.unreadCount === "number" ? data.unreadCount : 0;

        if (newCount > unreadCountRef.current) {
          // Increased: reset to avoid duplicates and refetch lists
          resetNotificationState();
          setUnreadCount(newCount);
          if (fetchListRef.current) {
            await fetchListRef.current("all", 1, false);
            if (activeTabRef.current === 1) {
              await fetchListRef.current("unseen", 1, false);
            }
          }
        } else if (newCount !== unreadCountRef.current) {
          // Decreased or other change: sync badge only
          setUnreadCount(newCount);
        }
      } catch (err) {
        console.error("Failed to poll unread count:", err);
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [resetNotificationState]);

  // Note: Unseen tab count uses server-provided unreadCount for accuracy

  const handleOpen = (event) => {
    // Open the drawer without refetching notifications
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      fetchList("unseen", 1, false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    // Optimistic update: mark all read and clear unseen list
    setLists((prev) => ({
      all: prev.all.map((n) => ({ ...n, is_read: true })),
      unseen: [],
    }));
    setUnreadCount(0);
    try {
      await api.patch("/api/notifications/mark-all-read");
    } catch (err) {
      // On failure, refetch to sync back
      console.error("Failed to mark all as read:", err);
      resetNotificationState();
      fetchList("all", 1, false);
      if (activeTab === 1) fetchList("unseen", 1, false);
      fetchUnreadCount();
    }
  };

  const handleNotificationClick = async (notification) => {
    const wasUnseen = !Boolean(notification.is_read);

    // Optimistic UI update across both lists
    setLists((prev) => ({
      all: prev.all.map((n) =>
        n.notification_id === notification.notification_id
          ? { ...n, is_read: true }
          : n
      ),
      unseen: prev.unseen
        .map((n) =>
          n.notification_id === notification.notification_id
            ? { ...n, is_read: true }
            : n
        )
        .filter((n) => !Boolean(n.is_read)),
    }));
    if (wasUnseen) {
      setUnreadCount((c) => Math.max(0, c - 1));
      // Fire-and-forget server mark-as-read
      try {
        await api.patch("/api/notifications/read", {
          notificationIds: [notification.notification_id],
        });
      } catch (err) {
        // On failure, log error; optionally could refetch counts/list
        console.error("Failed to mark notification as read:", err);
      }
    }

    if (notification?.redirect && notification?.rfi_id)
      navigate(`/viewRFI/${notification.rfi_id}`);

    handleClose();
  };

  const currentKey = activeTab === 0 ? "all" : "unseen";
  const filteredNotifications = lists[currentKey];

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} size="large">
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 480,
            maxWidth: "90vw",
            p: 0,
            borderRadius: 2,
            boxShadow: 6,
          },
        }}
      >
        <Box
          px={3}
          pt={2}
          pb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight={700}>
            Notifications
          </Typography>
          <Button
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Mark all as read
          </Button>
        </Box>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          variant="fullWidth"
          TabIndicatorProps={{
            sx: { bgcolor: (theme) => theme.palette.primary.main },
          }}
        >
          <Tab
            label={`ALL (${lists.all.length})`}
            sx={{
              fontWeight: 600,
              "&.Mui-selected": {
                color: (theme) => theme.palette.primary.main,
              },
            }}
          />
          <Tab
            label={`UNSEEN (${unreadCount})`}
            sx={{
              fontWeight: 600,
              "&.Mui-selected": {
                color: (theme) => theme.palette.primary.main,
              },
            }}
          />
        </Tabs>
        <Divider />
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          <List disablePadding>
            {loading[currentKey] && filteredNotifications.length === 0 ? (
              <ListItem sx={{ justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </ListItem>
            ) : filteredNotifications.length === 0 ? (
              <ListItem>
                <ListItemText primary="No notifications." />
              </ListItem>
            ) : (
              filteredNotifications.map((notification) => (
                <NotificationListItem
                  key={notification.notification_id || notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))
            )}
          </List>
        </Box>
        <Divider />
        <Box textAlign="center" py={1.5}>
          {hasMore[currentKey] ? (
            <Button
              size="small"
              variant="text"
              disabled={loadingMore[currentKey]}
              onClick={() => {
                if (loadingMore[currentKey]) return;
                if (currentKey === "all") {
                  fetchList("all", pages.all + 1, true);
                } else {
                  fetchList("unseen", pages.unseen + 1, true);
                }
              }}
              sx={{
                fontWeight: 600,
                minHeight: 24,
                gap: 1,
                display: "inline-flex",
                alignItems: "center",
              }}
              aria-busy={loadingMore[currentKey] || undefined}
            >
              {loadingMore[currentKey] ? (
                <CircularProgress size={18} />
              ) : (
                "Show more"
              )}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No more notifications
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
